import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { useState, useCallback, useMemo } from 'react'
import { motion } from 'framer-motion'
import {
  MessageSquare, PhoneIncoming, Send,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import { SearchInput } from '@/components/molecules/SearchInput'
import { PaginationBar } from '@/components/molecules/PaginationBar'
import { ListEmptyState } from '@/components/molecules/ListEmptyState'
import { ListSkeleton } from '@/components/molecules/ListSkeleton'
import { useUrlListState } from '@/hooks/useUrlListState'
import { listSearchSchema } from '@/lib/list-search-schema'
import { useSmsList, useSendSms } from '@/hooks/usePhone'
import { cn } from '@/lib/utils'
import type { SmsMessage } from '@/lib/api/phone'
import { useTranslation } from 'react-i18next'

const smsSearchSchema = listSearchSchema.extend({
  direction: z.enum(['inbound', 'outbound']).optional(),
})

export const Route = createFileRoute('/_authenticated/phone/sms')({
  validateSearch: smsSearchSchema,
  component: SmsTab,
})

function fmtTime(d: string | null): string {
  if (!d) return '—'
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function SmsTab() {
  const { t } = useTranslation()
  const list = useUrlListState({ from: '/_authenticated/phone/sms', defaultSort: 'createdAt' })
  const directionFilter = list.getFilter<string>('direction') ?? ''
  const [showSendDialog, setShowSendDialog] = useState(false)
  const [smsTo, setSmsTo] = useState('')
  const [smsBody, setSmsBody] = useState('')
  const sendSms = useSendSms()

  const params = useMemo(() => ({
    search: list.debouncedSearch || undefined,
    direction: directionFilter || undefined,
    page: list.page,
    perPage: list.perPage,
  }), [list.debouncedSearch, directionFilter, list.page, list.perPage])

  const { data, isLoading } = useSmsList(params)

  const handleSend = useCallback(() => {
    if (!smsTo || !smsBody) return
    sendSms.mutate({ to: smsTo, body: smsBody }, {
      onSuccess: () => { setShowSendDialog(false); setSmsTo(''); setSmsBody('') },
    })
  }, [smsTo, smsBody, sendSms])

  const directionOptions = [
    { key: '', label: t('phoneDashboard:all') },
    { key: 'inbound', label: t('phoneDashboard:inbound') },
    { key: 'outbound', label: t('phoneDashboard:outbound') },
  ]

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <SearchInput value={list.search} onChange={list.setSearch} placeholder={t('phoneDashboard:searchMessages')} className="flex-1" />
        <div className="flex items-center gap-1">
          {directionOptions.map(opt => (
            <Button key={opt.key} size="sm" variant={directionFilter === opt.key ? 'default' : 'outline'}
              onClick={() => list.setFilter('direction', opt.key || null)} className="text-xs">{opt.label}</Button>
          ))}
        </div>
        <Button size="sm" onClick={() => setShowSendDialog(true)}><Send className="w-4 h-4 mr-1" />{t('phoneDashboard:sendSms')}</Button>
      </div>
      {isLoading ? (
        <ListSkeleton rows={8} />
      ) : !data?.data?.length ? (
        <ListEmptyState icon={MessageSquare} title={t('phoneDashboard:noMessages')} description={t('phoneDashboard:smsMessagesWillAppearHere')} />
      ) : (
        <div className="space-y-2">
          {data.data.map((msg, i) => <SmsRow key={msg.id} msg={msg} index={i} />)}
        </div>
      )}
      {data?.pagination && (
        <PaginationBar page={data.pagination.page} totalPages={data.pagination.totalPages} total={data.pagination.total} perPage={data.pagination.perPage} onPage={list.setPage} onPerPage={list.setPerPage} />
      )}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('phoneDashboard:sendSms')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label htmlFor="sms-to">{t('phoneDashboard:to')}</Label><Input id="sms-to" placeholder="+1234567890" value={smsTo} onChange={e => setSmsTo(e.target.value)} /></div>
            <div><Label htmlFor="sms-body">{t('phoneDashboard:message')}</Label><Textarea id="sms-body" placeholder={t('phoneDashboard:typeYourMessage')} value={smsBody} onChange={e => setSmsBody(e.target.value)} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>{t('common:actions.cancel')}</Button>
            <Button onClick={handleSend} disabled={sendSms.isPending || !smsTo || !smsBody}>{sendSms.isPending ? t('phoneDashboard:sending') : t('phoneDashboard:send')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function SmsRow({ msg, index }: { msg: SmsMessage; index: number }) {
  const { t } = useTranslation()
  const isInbound = msg.direction === 'inbound'
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.02 }}
      className="flex items-start gap-3 rounded-xl border border-border/50 bg-card/50 backdrop-blur-sm p-4">
      <div className={cn('w-8 h-8 rounded-full flex items-center justify-center shrink-0', isInbound ? 'bg-green-500/15 text-green-600' : 'bg-purple-500/15 text-purple-600')}>
        {isInbound ? <PhoneIncoming className="w-4 h-4" /> : <Send className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{isInbound ? msg.fromNumber : msg.toNumber}</span>
          <Badge variant="outline" className="text-[10px]">{msg.status}</Badge>
          {msg.triggerType && msg.triggerType !== 'manual' && <Badge variant="secondary" className="text-[10px]">{msg.triggerType.replace('_', ' ')}</Badge>}
        </div>
        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{msg.body ?? t('phoneDashboard:noContent')}</p>
        <span className="text-xs text-muted-foreground">{fmtTime(msg.createdAt)}</span>
      </div>
    </motion.div>
  )
}
